import axiosClient from "api/axiosClient";
import AgencyHelper from "features/Agency/agencyHelper";
import KTLabel, { KTLabelSize } from "general/components/OtherKeenComponents/KTLabel";
import KTTable from "general/components/OtherKeenComponents/KTTable";
import tableStyle from "general/components/OtherKeenComponents/KTTable/tableStyle";
import KTTooltip from "general/components/OtherKeenComponents/KTTooltip";
import AppColors from "general/constants/AppColors";
import AppRoute from "general/constants/AppRoute";
import UIHelper from "general/helpers/UIHelper";
import Utils from "general/utils/Utils";
import moment from "moment";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

RelatedOrderView.propTypes = {
    item: PropTypes.object,
    onCancel: PropTypes.func,
};

function RelatedOrderView({ item, agencyDetail, onCancel }) {

    const [agencySaleList, setAgencySaleList] = useState([]);
    const [total, setTotal] = useState(0);

    const defaultColumnIds = useMemo(() => {
        return [
            'SALE_ID',
            'OPERATOR_ID',
            'USER_ID',
            'AGENCY_ID',
            'BUYER_ID',
            'ORDER_TYPE',
            'AMOUNT',
            'STATUS',
            'STATUS_CONTRACT',
            'CONTRACT',
            'POA_DOCUMENT',
            'CONTRACT_NUMBER',
            'TIME',
        ];
    }, []);

    const fullColumns = useMemo(() => {
        return [
            {
                id: 'SALE_ID',
                name: UIHelper.buildTableColumn('Mã đơn hàng'),
                selector: (row) => row?.saleCode ?? row?.saleId,
                width: '140px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'BUYER_ID',
                name: UIHelper.buildTableColumn('Nhà đầu tư/Chủ MB'),
                selector: (row) => (
                    <span
                        className="cursor-pointer hover-opacity-60"
                        onClick={() => {
                            if (row?.orderType === AgencyConstants.SaleOrderType.GROUP) {
                                Utils.openInNewTab(`${AppRoute.groupList}/${row?.buyerId}`);
                            } else if (row?.orderType === AgencyConstants.SaleOrderType.INVESTOR) {
                                Utils.openInNewTab(`${AppRoute.investorList}/${row?.buyerId}`);
                            }
                        }}
                    >
                        {row?.buyer?.name}
                    </span>
                ),
                minWidth: '240px',
                style: tableStyle.columns.link,
            },
            {
                id: 'ORDER_TYPE',
                name: UIHelper.buildTableColumn('Loại đơn hàng'),
                selector: (row) => AgencyHelper.Utils.getSaleOrderTypeName(row?.orderType),
                width: '180px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'AMOUNT',
                name: UIHelper.buildTableColumn('Tổng tiền', 'VNĐ'),
                selector: (row) => Utils.formatNumber(row?.amount ?? 0),
                right: true,
                width: '140px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'STATUS',
                name: UIHelper.buildTableColumn('Trạng thái đơn hàng'),
                selector: (row) => (
                    <KTLabel
                        text={AgencyHelper.Utils.getSaleOrderStatusName(row?.status)}
                        isInline={true}
                        size={KTLabelSize.medium}
                        additionalClassName={`label-light-${AgencyHelper.Utils.getSaleOrderStatusColor(
                            row?.status
                        )}`}
                    />
                ),
                minWidth: '200px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'STATUS_CONTRACT',
                name: UIHelper.buildTableColumn('Trạng thái hợp đồng'),
                selector: (row) => {
                    return !!_.first(row?.contracts) ? (
                        <KTLabel
                            text={AgencyHelper.Utils.getContractStatusName(_.first(row?.contracts)?.status)}
                            isInline={true}
                            size={KTLabelSize.medium}
                            additionalClassName={`label-light-${AgencyHelper.Utils.getContractStatusColor(
                                _.first(row?.contracts)?.status
                            )}`}
                            additionalDescription={_.first(row?.contracts)?.cancelingReason}
                        />
                    ) : (
                        <></>
                    );
                },
                minWidth: '160px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'CONTRACT_NUMBER',
                name: UIHelper.buildTableColumn('Số hợp đồng'),
                selector: (row) => row?.contractNumber,
                width: '160px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'USER_ID',
                name: UIHelper.buildTableColumn('Người tạo đơn'),
                selector: (row) => (
                    <span className="cursor-pointer hover-opacity-60" onClick={() => { }}>
                        {row?.user?.fullname}
                    </span>
                ),
                minWidth: '240px',
                style: tableStyle.columns.link,
            },
            {
                id: 'TIME',
                name: UIHelper.buildTableColumn('Ngày tạo'),
                selector: (row) => Utils.formatDateTimeToString(row?.time, 'DD/MM/YYYY - HH:mm'),
                width: '160px',
                style: tableStyle.columns.normal,
            },
            {
                name: UIHelper.buildTableColumn('Thao tác'),
                maxWidth: '140px',
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #ebedfe',
                },
                center: false,
                cell: (row) => (
                    <div className="d-flex align-items-center gap-2">
                        <KTTooltip text={'Xem chi tiết'}>
                            <a
                                className="btn btn-sm btn-clean btn-icon"
                                onClick={() => {
                                    Utils.openInNewTab(`${AppRoute.orderList}/${row?.saleId}`)
                                }}
                            >
                                <i
                                    className="fa-regular fa-eye"
                                    style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                ></i>
                            </a>
                        </KTTooltip>
                    </div>
                )
            },
        ];
    }, []);
    const [filter, setFilter] = useState();
    useEffect(() => {
        const selectedMonth = item?.time?.month;
        const from = moment(selectedMonth, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
        const to = moment(selectedMonth, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');

        setFilter({
            agencyId: agencyDetail?.agencyId,
            status: 'COMPLETED',
            from,
            to,
        });
    }, []);

    const requestGetAgencySaleList = async (params) => {
        try {
            const res = await axiosClient.get('/sale', { params: params });
            const { result, sales, total } = res;
            if (result === 'success') {
                setAgencySaleList(sales);
                setTotal(total);
            }
        } catch (error) { }
    };

    useEffect(() => {
        if (Utils.isValid(filter)) {
            let params = { ...filter };
            requestGetAgencySaleList(params);
        }
    }, [filter]);

    return (
        <div className="">
            <div className="p-4">
                <KTTable
                    columns={fullColumns}
                    items={agencySaleList}
                    selectableRows={false}
                    striped={false}
                    compactGrid={false}
                    onClickedRow={(row) => { }}
                    onSelectedRowsChanged={(rows) => { }}
                    onDoubleClickedRow={(row) => { }}
                    noDataComponent={UIHelper.buildEmptyNoData(
                        'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                    )}
                />
            </div>
        </div>
    );
}

export default RelatedOrderView;